import React from 'react';
import _isEmpty from 'lodash/isEmpty';
import _startCase from 'lodash/startCase';
import { makeStyles } from 'design-system';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import API from '@scale/frontend-lib/dashboard-api';
import { useProjectInstructionContext } from '../projectInstructionContext';

const OnboardingBanner = ({ NewLabel, title }: { NewLabel: React.FC; title: string }) => (
  <>
    <div>
      <strong>Welcome to {_startCase(title)} </strong>
    </div>
    <div>
      To get started, please read through all the <NewLabel /> instructions before proceeding.{' '}
    </div>
  </>
);

const NewVersionBanner = ({
  NewLabel,
  ChangedLabel,
}: {
  NewLabel: React.FC;
  ChangedLabel: React.FC;
}) => (
  <>
    <div>
      <strong>New instructions available</strong>
    </div>
    <div>
      You are about to work on a task with a <b>different version of the instructions</b> than the
      previous task on which you worked.
    </div>
    <div>
      Please carefully read through all the <NewLabel /> and <ChangedLabel /> instructions before
      proceeding.
    </div>
  </>
);

const Banner = () => {
  const {
    isOnboarding,
    hasVersionComparison,
    title = '',
    extraAlerts,
    isMobileView,
  } = useProjectInstructionContext();
  const classes = useStyles({ isOnboarding: !!isOnboarding, hasVersionComparison });

  if (!isOnboarding && !hasVersionComparison && !_isEmpty(extraAlerts)) {
    return null;
  }

  const { hasSensitiveContent, isEdgeCase, param_chunks } = extraAlerts;

  const NewLabel = () => {
    return <span className={classes.newLabel}>New</span>;
  };
  const ChangedLabel = () => {
    return <span className={classes.newLabel}>Changed</span>;
  };

  const disableAdultContent = async () => {
    const { isWorker } = await API.selfserve.adultContentToggle({ blockAdultContent: true });
    if (isWorker) {
      window.location.href = '/';
    }
  };

  return (
    <div className={classes.container} style={{ borderRadius: isMobileView ? 0 : 10 }}>
      <div>
        <FontAwesomeIcon icon="flag" color="white" />
      </div>
      <div>
        {isOnboarding && <OnboardingBanner NewLabel={NewLabel} title={title} />}
        {hasVersionComparison && (
          <NewVersionBanner NewLabel={NewLabel} ChangedLabel={ChangedLabel} />
        )}
        {param_chunks && (
          <li>
            This project contains different types of tasks. Please refer to the relevant instruction
            sections for the questions and annotations you are given.
          </li>
        )}
        {isEdgeCase && (
          <li>
            This is an edge case detection task. The primary goal is to determine whether the
            following instructions are detailed and specific enough to complete the task WITHOUT
            AMBIGUITY. Please provide your feedback in the "edge case feedback" modal at the bottom
            of the task page.
          </li>
        )}
        {hasSensitiveContent && (
          <li>
            WARNING: this project may contain sensitive content. If you're not comfortable with
            this, please <a onClick={disableAdultContent}>exit now</a>
            to work on other projects.
          </li>
        )}
      </div>
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  container: {
    background: ({
      isOnboarding,
      hasVersionComparison,
    }: {
      isOnboarding: boolean;
      hasVersionComparison: boolean;
    }) =>
      isOnboarding
        ? theme.colors.green
        : hasVersionComparison
        ? theme.colors.blue
        : theme.colors.gray10,
    padding: 15,
    display: 'flex',
    '& > div': {
      color: 'white',
    },
    '& > div:first-child': {
      paddingRight: 20,
    },
    marginBottom: 10,
    [theme.breakpoints.down('sm')]: {
      borderRadius: 0,
    },
  },

  newLabel: {
    color: ({ isOnboarding }: { isOnboarding: boolean }) =>
      isOnboarding ? theme.colors.green : theme.colors.blue,
    borderRadius: 30,
    background: 'white',
    fontSize: 10,
    fontWeight: 500,
    padding: '2px 5px',
    margin: '0 2px',
  },
}));

export default Banner;
